// PPTHeroBainScene.js - Bain & Company specific extensions
import PPTHeroBaseScene from '../PPTHeroBaseScene.js';
import BainConfig from './BainConfig.js';
import ShortcutManager from '../modules/ShortcutManager.js';
import LevelManager from '../modules/LevelManager.js';
import AnalyticsManager from '../modules/AnalyticsManager.js';

export default class PPTHeroBainScene extends PPTHeroBaseScene {
  constructor() {
    super({
      key: 'PPTHeroBainScene'
    });
    this.firmType = 'BAIN';
    this.bainConfig = BainConfig;
  }

  // Initialize Bain-specific data
  init(data) {
    // Call parent init first
    super.init(data);
    
    // Set user profile with Bain access permissions
    this.setUserProfile({
      firmAccess: ['bain'],
      permissions: ['basic', 'bain'],
      firm: 'bain'
    });
    
    console.log('👤 Bain user profile set with access to:', this.userProfile.firmAccess);
    
    // Ensure the ScoreStorageManager has the correct user context for Bain tiles
    if (this.scoreStorageManager) {
      this.scoreStorageManager.userContext = this.userProfile;
    }
    
    // Override the ShortcutManager with Bain-specific firm type
    this.shortcutManager = new ShortcutManager(this, 'BAIN');
    console.log('🔧 Bain ShortcutManager initialized with BAIN firm type');
    
    // Note: Browser shortcut prevention is now handled by parent class via shouldPreventBrowserShortcut override
  }

  // Override create to add Bain-specific functionality
  create() {
    super.create();
    
    console.log("🔧 PPTHeroBainScene create: About to call setupLevels");
    
    // Call setupLevels to add Bain-specific levels
    this.setupLevels();
    
    console.log("🔧 PPTHeroBainScene create: setupLevels completed. Levels array length:", this.levels?.length);
    
    // Apply Bain color scheme and create Bain sidebar
    this.time.delayedCall(100, () => {
      this.applyBainColorScheme();
      this.createBainSidebar();
    });
    
    console.log("PPTHeroBainScene created with Bain-specific features");
  }

  // Override startLevel to add Bain-specific input management
  startLevel(levelIndex) {
    super.startLevel(levelIndex);
    
    // If this is a Bain level (22-30), set up enhanced input capture
    if (levelIndex >= 22 && levelIndex <= 30) {
      this.setupBainLevelInputCapture();
    }
  }

  // Set up enhanced input capture for Bain levels using Rex UI
  setupBainLevelInputCapture() {
    console.log('🎯 Setting up Bain level input capture for enhanced browser shortcut prevention');
    
    // Create an invisible full-screen overlay to capture all input
    this.bainInputOverlay = this.rexUI.add.overlapSizer({
      x: 0, y: 0,
      width: this.cameras.main.width,
      height: this.cameras.main.height
    });
    
    // Make it invisible but interactive
    this.bainInputOverlay.setAlpha(0);
    this.bainInputOverlay.setDepth(-50); // Below everything else
    this.bainInputOverlay.setInteractive();
    
    // Add aggressive keyboard event capture
    this.bainInputOverlay.on('pointerdown', () => {
      // Ensure canvas has focus when user interacts
      if (this.game && this.game.canvas) {
        this.game.canvas.focus();
      }
    });
    
    console.log('✅ Bain input capture overlay created');
  }

  // Clean up Bain input capture when level ends
  clearBainLevelInputCapture() {
    if (this.bainInputOverlay) {
      this.bainInputOverlay.destroy();
      this.bainInputOverlay = null;
      console.log('🧹 Cleaned up Bain input capture overlay');
    }
  }

  // Override levelCleared to clean up Bain-specific resources
  levelCleared() {
    this.clearBainLevelInputCapture();
    super.levelCleared();
  }

  // Note: Shortcuts are now defined in ShortcutsConfig.js and handled by ShortcutManager with BAIN firm type

  // Override to provide Bain-specific shortcut descriptions for popup
  getShortcutDescriptions() {
    // Import shortcut formatter
    const ShortcutFormatter = this.ShortcutFormatter || window.ShortcutFormatter;
    
    // Generic shortcuts with correct sequential formatting
    const genericShortcuts = {
      'Ctrl+A': 'Select All',
      'Alt,H,G,A,L': 'Align Left',
      'Alt,H,G,A,R': 'Align Right', 
      'Alt,H,G,A,T': 'Align Top',
      'Alt,H,G,A,B': 'Align Bottom',
      'Alt,H,G,A,C': 'Center Horizontally',
      'Alt,H,G,A,M': 'Center Vertically',
      'Alt,H,G,A,H': 'Distribute Horizontally',
      'Alt,H,G,A,V': 'Distribute Vertically',
      'Ctrl+Shift+[': 'Send to Back',
      'Ctrl+Shift+]': 'Bring to Front'
    };
    
    // Add Bain-specific shortcuts with correct sequential formatting
    return {
      ...genericShortcuts,
      '--- BAIN SHORTCUTS ---': '',
      'Alt,B,R,A,P,I,D': 'Apply RAPID Framework',
      'Alt,B,V,C': 'Apply Value Chain Analysis', 
      'Alt,B,S,W,O,T': 'Apply SWOT Analysis',
      'Alt,B,M,P,S': 'Apply Master Presentation Structure',
      'Ctrl+Shift+O': 'Add Sticker',
      'Ctrl+Shift+A': 'Add Status Sticker',
      'Ctrl+Shift+B': 'Add Bain Bullets',
      'Ctrl+Shift+H': 'Add Headers',


      'Alt,X,S,H': 'Standardize Heights',
      'Alt,X,S,W': 'Standardize Widths'
    };
  }

  // Override to use Bain-specific rounds structure
  getRoundsDefinition() {
    return [
      {
        id: 'practice',
        name: 'Practice',
        levels: [0],
        description: 'Warm up and learn the basics', // Match Generic mode
        canReplay: true,
        color: 0x95a5a6 // Gray
      },
      {
        id: 'calibration',
        name: 'Calibration Round', // Match Generic mode
        levels: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
        description: 'Test your skills and get calibrated', // Match Generic mode
        canReplay: false, // Can only be played once - sets baseline
        color: 0x3498db // Blue
      },
      {
        id: 'basic',
        name: 'Basic Round', // Match Generic mode
        levels: [2, 5, 1, 7, 4, 8, 3, 9, 6, 10], // Same levels, different order
        description: 'Standard difficulty challenges', // Match Generic mode
        canReplay: true,
        color: 0x2ecc71 // Green
      },
      {
        id: 'advanced',
        name: 'Advanced Round', // Match Generic mode
        levels: [11, 12, 13, 14, 15, 16, 17, 18, 19, 20],
        description: 'Expert level challenges', // Match Generic mode
        canReplay: true,
        color: 0xe74c3c // Red
      },
      {
        id: 'bain-basic',
        name: 'Bain Basic',
        levels: [22, 23, 24, 25, 28, 29], // 6 Bain-specific levels testing Bain shortcuts - Levels 26,27 removed
        description: 'Bain fundamentals',
        canReplay: true,
        color: 0xc41e3a, // Bain red
        firmSpecific: true
      },
      {
        id: 'bain-advanced',
        name: 'Bain Advanced',
        levels: [31, 32, 33, 34, 35, 36, 37, 38, 39, 40], // Advanced Bain levels
        description: 'Bain frameworks & efficiency',
        canReplay: true,
        color: 0x8b1538, // Darker Bain red
        firmSpecific: true
      }
    ];
  }

  // Bain-specific shortcut methods
  applyRAPIDFramework() {
    const selectedShapes = this.selectedShapes || [];
    if (selectedShapes.length === 0) return;
    
    // Arrange shapes in RAPID framework layout
    if (selectedShapes.length >= 5) {
      const positions = [
        { x: 400, y: 150 }, // Recommend (center top)
        { x: 200, y: 250 }, // Agree (left)
        { x: 600, y: 250 }, // Perform (right)
        { x: 300, y: 350 }, // Input (bottom left)
        { x: 500, y: 350 }  // Decide (bottom right)
      ];
      
      selectedShapes.slice(0, 5).forEach((shape, index) => {
        shape.x = positions[index].x;
        shape.y = positions[index].y;
      });
    }
    
    this.trackKeystrokes();
    this.trackShortcutUsed("alt,b,r,a,p,i,d");
    this.analyticsManager?.trackShortcutUsage("alt,b,r,a,p,i,d", "RAPID Framework");
  }

  applyValueChain() {
    const selectedShapes = this.selectedShapes || [];
    if (selectedShapes.length === 0) return;
    
    // Arrange shapes in value chain sequence
    selectedShapes.forEach((shape, index) => {
      shape.x = 150 + (index * 120);
      shape.y = 200;
    });
    
    this.trackKeystrokes();
    this.trackShortcutUsed("alt,b,v,c");
    this.analyticsManager?.trackShortcutUsage("alt,b,v,c", "Value Chain Analysis");
  }

  applySWOTAnalysis() {
    const selectedShapes = this.selectedShapes || [];
    if (selectedShapes.length === 0) return;
    
    // Arrange shapes in SWOT 2x2 matrix
    if (selectedShapes.length >= 4) {
    const positions = [
        { x: 450, y: 200 }, // Strengths (top-left)
        { x: 650, y: 200 }, // Weaknesses (top-right)
        { x: 450, y: 350 }, // Opportunities (bottom-left)
        { x: 650, y: 350 }  // Threats (bottom-right)
      ];
      
      selectedShapes.slice(0, 4).forEach((shape, index) => {
        shape.x = positions[index].x;
        shape.y = positions[index].y;
    });
    }
    
    this.trackKeystrokes();
    this.trackShortcutUsed("alt,b,s,w,o,t");
    this.analyticsManager?.trackShortcutUsage("alt,b,s,w,o,t", "SWOT Analysis");
  }

  applyMasterPresentation() {
    const selectedShapes = this.selectedShapes || [];
    if (selectedShapes.length === 0) return;
    
    // Arrange shapes in master presentation structure
    selectedShapes.forEach((shape, index) => {
      if (index === 0) {
        shape.x = 400; shape.y = 100; // Title
      } else if (index < 4) {
        shape.x = 200 + ((index - 1) * 200); shape.y = 200; // Main points
      } else {
        shape.x = 400; shape.y = 350; // Conclusion
      }
    });
    
    this.trackKeystrokes();
    this.trackShortcutUsed("alt,b,m,p,s");
    this.analyticsManager?.trackShortcutUsage("alt,b,m,p,s", "Master Presentation Structure");
  }

  // Bain-specific shortcut implementations for new levels
  addSticker() {
    console.log('🏷️ Adding Bain sticker - START');
    console.log('🔍 Current slideArea:', this.slideArea);
    console.log('🔍 Current depth hierarchy - white canvas should be at -100, elements at 500');
    this.trackShortcutUsed("ctrl,shift,o");
    
    // Get the actual username from the user profile
    const userName = this.userProfile?.name || 'User';
    console.log('👤 Creating sticker for user:', userName);
    
    // Create a speech bubble as a proper shape that can be moved
    const speechBubble = this.add.graphics();
    speechBubble.fillStyle(0xffffff, 1);
    speechBubble.lineStyle(2, 0x000000, 1);
    
    // Use the actual slideArea from the parent scene for correct positioning
    const slideArea = this.slideArea;
    if (!slideArea) {
      console.error('❌ slideArea not available - cannot position sticker correctly');
      return;
    }
    
    // Position in top right of the actual white canvas area
    const bubbleX = slideArea.right - 200; // 200px from right edge of canvas
    const bubbleY = slideArea.top + 50; // 50px from top of canvas
    const bubbleWidth = 180;
    const bubbleHeight = 80;
    
         console.log('📍 Positioning speech bubble at:', { bubbleX, bubbleY, slideArea: this.slideArea });
    
    // Draw speech bubble with rounded corners
    speechBubble.fillRoundedRect(bubbleX, bubbleY, bubbleWidth, bubbleHeight, 10);
    speechBubble.strokeRoundedRect(bubbleX, bubbleY, bubbleWidth, bubbleHeight, 10);
    
    // Add tail pointing to the right
    speechBubble.fillTriangle(
      bubbleX + bubbleWidth, bubbleY + 20,
      bubbleX + bubbleWidth + 15, bubbleY + 30,
      bubbleX + bubbleWidth, bubbleY + 40
    );
    speechBubble.strokeTriangle(
      bubbleX + bubbleWidth, bubbleY + 20,
      bubbleX + bubbleWidth + 15, bubbleY + 30,
      bubbleX + bubbleWidth, bubbleY + 40
    );
    
    // Add text inside the bubble with proper positioning
    const commentText = this.add.text(bubbleX + 10, bubbleY + 10, `${userName}\nComment Box`, {
      fontSize: '14px',
      color: '#000000',
      fontFamily: 'Arial, sans-serif',
      align: 'left'
    });
    
    // Set proper depth to ensure visibility above white canvas (-100) but below UI elements (1000+)
    // White canvas is at -100, UI elements at 1000+, so we use 100 for game elements
    const gameElementDepth = 100;
    speechBubble.setDepth(gameElementDepth);
    commentText.setDepth(gameElementDepth + 1);
    
    console.log('🎯 Speech bubble and text set to depth:', gameElementDepth);
    
    // Make the speech bubble interactive and selectable (inherits PowerPoint behavior)
    speechBubble.setInteractive(new Phaser.Geom.Rectangle(bubbleX, bubbleY, bubbleWidth, bubbleHeight), Phaser.Geom.Rectangle.Contains);
    speechBubble.setData('shapeType', 'comment-box');
    speechBubble.isDecoration = false; // Allow selection
    speechBubble.originalFillColor = 0xffffff;
    
    // Add drag functionality
    this.input.setDraggable(speechBubble);
    
    // Add click events for selection (inherits base scene PowerPoint behavior)
    speechBubble.on('pointerdown', (pointer) => {
      // Check keyboard state directly
      const isShiftHeld = this.input.keyboard.checkDown(this.input.keyboard.addKey('SHIFT'));
      const isCtrlHeld = this.input.keyboard.checkDown(this.input.keyboard.addKey('CTRL'));
      const isMultiSelect = isShiftHeld || isCtrlHeld;
      
      this.selectShape(speechBubble, isMultiSelect);
    });
    
    // Add drag events (inherits base scene group movement)
    speechBubble.on('dragstart', (pointer) => {
      this.shapeManager.onDragStart(pointer, speechBubble);
    });
    
    speechBubble.on('drag', (pointer, dragX, dragY) => {
      this.shapeManager.onDrag(pointer, speechBubble, dragX, dragY);
    });
    
    speechBubble.on('dragend', (pointer) => {
      this.shapeManager.onDragEnd(pointer, speechBubble);
    });
    
    // Add to the scene's shapes collection so it's treated like other shapes
    if (!this.allShapes) {
      this.allShapes = [];
    }
    this.allShapes.push(speechBubble);
    this.allShapes.push(commentText); // Also add text for proper cleanup
    
    console.log('✅ Speech bubble created and added to allShapes. Total shapes:', this.allShapes.length);
    console.log('✅ Speech bubble depth:', speechBubble.depth);
    console.log('✅ Comment text depth:', commentText.depth);
    console.log('✅ Speech bubble position:', { x: speechBubble.x, y: speechBubble.y });
    
    // Show success notification
    this.showCenterNotification('✅ Sticker Added!', 'success', 1500);
    
    // Don't auto-complete - let goal condition determine completion
  }

  addStatusSticker() {
    console.log('📊 Adding status sticker');
    this.trackShortcutUsed("ctrl,shift,a");
    
    // Create a status sticker as a proper shape that can be moved
    const statusSticker = this.add.graphics();
    statusSticker.fillStyle(0xff6b6b, 1); // Red status color
    
    // Use the actual slideArea from the parent scene for correct positioning
    const slideArea = this.slideArea;
    if (!slideArea) {
      console.error('❌ slideArea not available - cannot position status sticker correctly');
      return;
    }
    
    // Position in top right of the actual white canvas area, below the first sticker
    const stickerX = slideArea.right - 150; // 150px from right edge of canvas
    const stickerY = slideArea.top + 150; // 150px from top of canvas, below the first sticker
    const stickerSize = 60;
    
    console.log('📍 Positioning status sticker at:', { stickerX, stickerY });
    
    // Draw circle
    statusSticker.fillCircle(stickerX + stickerSize/2, stickerY + stickerSize/2, stickerSize/2);
    statusSticker.lineStyle(2, 0x000000, 1);
    statusSticker.strokeCircle(stickerX + stickerSize/2, stickerY + stickerSize/2, stickerSize/2);
    
    // Add text inside
    const statusText = this.add.text(stickerX + stickerSize/2, stickerY + stickerSize/2, 'STATUS', {
      fontSize: '12px',
      color: '#ffffff',
      fontFamily: 'Arial, sans-serif',
      align: 'center'
    });
    statusText.setOrigin(0.5);
    
    // Set proper depth to ensure visibility above white canvas (-100) but below UI elements (1000+)
    // White canvas is at -100, UI elements at 1000+, so we use 100 for game elements
    const gameElementDepth = 100;
    statusSticker.setDepth(gameElementDepth);
    statusText.setDepth(gameElementDepth + 1);
    
    console.log('🎯 Status sticker and text set to depth:', gameElementDepth);
    
    // Make the status sticker interactive and draggable
    statusSticker.setInteractive(new Phaser.Geom.Circle(stickerX + stickerSize/2, stickerY + stickerSize/2, stickerSize/2), Phaser.Geom.Circle.Contains);
    this.input.setDraggable(statusSticker);
    
    // Handle drag events
    this.input.on('drag', (pointer, gameObject, dragX, dragY) => {
      if (gameObject === statusSticker) {
        // Update the graphics position
        statusSticker.clear();
        statusSticker.fillStyle(0xff6b6b, 1);
        statusSticker.lineStyle(2, 0x000000, 1);
        
        // Redraw at new position
        statusSticker.fillCircle(dragX + stickerSize/2, dragY + stickerSize/2, stickerSize/2);
        statusSticker.strokeCircle(dragX + stickerSize/2, dragY + stickerSize/2, stickerSize/2);
        
        // Update text position
        statusText.setPosition(dragX + stickerSize/2, dragY + stickerSize/2);
        
        // Update interactive area
        statusSticker.setInteractive(new Phaser.Geom.Circle(dragX + stickerSize/2, dragY + stickerSize/2, stickerSize/2), Phaser.Geom.Circle.Contains);
      }
    });
    
    // Add to the scene's shapes collection
    if (!this.allShapes) {
      this.allShapes = [];
    }
    this.allShapes.push(statusSticker);
    this.allShapes.push(statusText); // Also add text for proper cleanup
    
    // Show success notification
    this.showCenterNotification('✅ Status Sticker Added!', 'success', 1500);
    
    // Don't auto-complete - let goal condition determine completion
  }



  addBainBullets() {
    console.log('🔸 Adding Bain bullets');
    this.trackShortcutUsed("ctrl,shift,b");
    
    // Create Bain-style bullet points as a proper shape that can be moved
    const bulletContainer = this.add.graphics();
    bulletContainer.fillStyle(0xffffff, 1);
    bulletContainer.lineStyle(2, 0x000000, 1);
    
    // Use the actual slideArea from the parent scene for correct positioning
    const slideArea = this.slideArea;
    if (!slideArea) {
      console.error('❌ slideArea not available - cannot position bullets correctly');
      return;
    }
    
    // Position in top right of the actual white canvas area, below the sources text box
    const containerX = slideArea.right - 200; // 200px from right edge of canvas
    const containerY = slideArea.top + 350; // 350px from top of canvas, below the sources text box
    const containerWidth = 180;
    const containerHeight = 100;
    
    console.log('📍 Positioning Bain bullets at:', { containerX, containerY });
    
    // Draw container
    bulletContainer.fillRoundedRect(containerX, containerY, containerWidth, containerHeight, 10);
    bulletContainer.strokeRoundedRect(containerX, containerY, containerWidth, containerHeight, 10);
    
    // Add bullet points
    const bulletPoints = [
      '• Key Insight 1',
      '• Key Insight 2', 
      '• Key Insight 3'
    ];
    
    const bulletTexts = [];
    bulletPoints.forEach((point, index) => {
      const bulletText = this.add.text(containerX + 20, containerY + 20 + (index * 25), point, {
        fontSize: '14px',
        color: '#000000',
        fontFamily: 'Arial, sans-serif',
        align: 'left'
      });
      bulletTexts.push(bulletText);
    });
    
    // Set proper depth to ensure visibility above white canvas (-100) but below UI elements (1000+)
    // White canvas is at -100, UI elements at 1000+, so we use 100 for game elements
    const gameElementDepth = 100;
    bulletContainer.setDepth(gameElementDepth);
    bulletTexts.forEach(text => text.setDepth(gameElementDepth + 1));
    
    console.log('🎯 Bain bullets container and text set to depth:', gameElementDepth);
    
    // Make the bullet container interactive and draggable
    bulletContainer.setInteractive(new Phaser.Geom.Rectangle(containerX, containerY, containerWidth, containerHeight), Phaser.Geom.Rectangle.Contains);
    this.input.setDraggable(bulletContainer);
    
    // Handle drag events
    this.input.on('drag', (pointer, gameObject, dragX, dragY) => {
      if (gameObject === bulletContainer) {
        // Update the graphics position
        bulletContainer.clear();
        bulletContainer.fillStyle(0xffffff, 1);
        bulletContainer.lineStyle(2, 0x000000, 1);
        
        // Redraw at new position
        bulletContainer.fillRoundedRect(dragX, dragY, containerWidth, containerHeight, 10);
        bulletContainer.strokeRoundedRect(dragX, dragY, containerWidth, containerHeight, 10);
        
        // Update text positions
        bulletTexts.forEach((text, index) => {
          text.setPosition(dragX + 20, dragY + 20 + (index * 25));
        });
        
        // Update interactive area
        bulletContainer.setInteractive(new Phaser.Geom.Rectangle(dragX, dragY, containerWidth, containerHeight), Phaser.Geom.Rectangle.Contains);
      }
    });
    
    // Add to the scene's shapes collection
    if (!this.allShapes) {
      this.allShapes = [];
    }
    this.allShapes.push(bulletContainer);
    bulletTexts.forEach(text => this.allShapes.push(text)); // Also add text for proper cleanup
    
    // Show success notification
    this.showCenterNotification('✅ Bain Bullets Added!', 'success', 1500);
    
    // Don't auto-complete - let goal condition determine completion
  }

  addHeaders() {
    console.log('📋 Adding headers to slide');
    this.trackShortcutUsed("ctrl,shift,h");
    
    // Create professional headers as a proper shape that can be moved
    const headerContainer = this.add.graphics();
    headerContainer.fillStyle(0x2c3e50, 1); // Dark blue header color
    
    // Use the actual slideArea from the parent scene for correct positioning
    const slideArea = this.slideArea;
    if (!slideArea) {
      console.error('❌ slideArea not available - cannot position headers correctly');
      return;
    }
    
    // Position at top center of the actual white canvas area
    const containerX = slideArea.x - 150; // Center of canvas
    const containerY = slideArea.top + 50; // 50px from top of canvas
    const containerWidth = 300;
    const containerHeight = 50;
    
    console.log('📍 Positioning headers at:', { containerX, containerY });
    
    // Draw header bar
    headerContainer.fillRoundedRect(containerX, containerY, containerWidth, containerHeight, 8);
    headerContainer.lineStyle(2, 0x000000, 1);
    headerContainer.strokeRoundedRect(containerX, containerY, containerWidth, containerHeight, 8);
    
    // Add header text
    const headerText = this.add.text(containerX + containerWidth/2, containerY + containerHeight/2, 'EXECUTIVE SUMMARY', {
      fontSize: '18px',
      color: '#ffffff',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold',
      align: 'center'
    });
    headerText.setOrigin(0.5);
    
    // Set proper depth to ensure visibility above white canvas (-100) but below UI elements (1000+)
    // White canvas is at -100, UI elements at 1000+, so we use 100 for game elements
    const gameElementDepth = 100;
    headerContainer.setDepth(gameElementDepth);
    headerText.setDepth(gameElementDepth + 1);
    
    console.log('🎯 Headers container and text set to depth:', gameElementDepth);
    
    // Make the header container interactive and draggable
    headerContainer.setInteractive(new Phaser.Geom.Rectangle(containerX, containerY, containerWidth, containerHeight), Phaser.Geom.Rectangle.Contains);
    this.input.setDraggable(headerContainer);
    
    // Handle drag events
    this.input.on('drag', (pointer, gameObject, dragX, dragY) => {
      if (gameObject === headerContainer) {
        // Update the graphics position
        headerContainer.clear();
        headerContainer.fillStyle(0x2c3e50, 1);
        headerContainer.lineStyle(2, 0x000000, 1);
        
        // Redraw at new position
        headerContainer.fillRoundedRect(dragX, dragY, containerWidth, containerHeight, 8);
        headerContainer.strokeRoundedRect(dragX, dragY, containerWidth, containerHeight, 8);
        
        // Update text position
        headerText.setPosition(dragX + containerWidth/2, dragY + containerHeight/2);
        
        // Update interactive area
        headerContainer.setInteractive(new Phaser.Geom.Rectangle(dragX, dragY, containerWidth, containerHeight), Phaser.Geom.Rectangle.Contains);
      }
    });
    
    // Add to the scene's shapes collection
    if (!this.allShapes) {
      this.allShapes = [];
    }
    this.allShapes.push(headerContainer);
    this.allShapes.push(headerText); // Also add text for proper cleanup
    
    // Show success notification
    this.showCenterNotification('✅ Headers Added!', 'success', 1500);
    
    // Don't auto-complete - let goal condition determine completion
  }

  // addBainTable method removed - CTRL+SHIFT+T conflicts with browser shortcut

  // selectSimilarShapes method removed - Level 27 disabled

  standardizeHeights() {
    this.trackShortcutUsed("alt,x,s,h");
    
    // Use SelectionManager for standardization operation
    const success = this.selectionManager.standardizeHeights();
    
    if (success) {
      // Check for level completion after standardization
      this.time.delayedCall(100, () => {
        if (this.selectionManager.checkHeightStandardization(28)) {
          this.levelCleared();
        }
      });
    }
  }

  standardizeWidths() {
    this.trackShortcutUsed("alt,x,s,w");
    
    // Use SelectionManager for standardization operation
    const success = this.selectionManager.standardizeWidths();
    
    if (success) {
      // Check for level completion after standardization
      this.time.delayedCall(100, () => {
        if (this.selectionManager.checkWidthStandardization(29)) {
          this.levelCleared();
        }
      });
    }
  }

  // Removed duplicate methods - now using SelectionManager.standardizeHeights/Widths and check methods

  // Override create to apply Bain color scheme and rounds
  create() {
    // Call parent to create Rex UI first
    super.create();
    
    // Apply Bain customizations after Rex UI is fully created
    this.time.delayedCall(100, () => {
      // Apply Bain color scheme to all HUD elements
      this.applyBainColorScheme();
      
      // Override the scrollable sidebar to include Bain rounds
      this.createBainScrollableSidebar();
    });
    
    // Add Bain visual branding (positioned below Rex UI top bar)
    this.bainBranding = this.add.text(
      this.rexUIManager.sidebarWidth + 20, 
      120, // Below the Rex UI top bar
      "🏢 Bain & Company Edition", 
      {
        ...this.getProfessionalTextStyle("20px", "#ffffff", "700"),
        backgroundColor: "#c41e3a", // Bain red
        padding: { x: 12, y: 6 }
      }
    );
    this.bainBranding.setDepth(1000); // Ensure it's visible

    // Add Bain mode indicator in top right (below Rex UI top bar)
    this.bainModeIndicator = this.add.text(
      this.cameras.main.width - 15, 
      120, // Below the Rex UI top bar
      "BAIN MODE", 
      {
        ...this.getProfessionalTextStyle("18px", "#ffffff", "700"),
        backgroundColor: "#c41e3a",
        padding: { x: 10, y: 5 }
      }
    );
    this.bainModeIndicator.setOrigin(1, 0); // Right-aligned
    this.bainModeIndicator.setDepth(1000);
  }

  // Apply Bain color scheme to all HUD elements
  applyBainColorScheme() {
    console.log('🎨 Applying Bain color scheme to HUD elements');
    
    const bainPrimary = 0xc41e3a; // Bain red
    const bainSecondary = 0x8b1538; // Darker Bain red
    
    // Update top bar background
    if (this.rexUIManager.topBar) {
      const background = this.rexUIManager.topBar.getElement('background');
      if (background) {
        console.log('🎨 Updating top bar to Bain red');
        background.setFillStyle(bainPrimary);
      } else {
        console.log('❌ Top bar background element not found');
      }
    } else {
      console.log('❌ Top bar not found');
    }
    
    // Update bottom bar background - try different access methods
    if (this.rexUIManager.bottomBar) {
      // Try multiple ways to access background
      let background = this.rexUIManager.bottomBar.getElement('background');
      if (!background) {
        background = this.rexUIManager.bottomBar.background;
      }
      if (!background && this.rexUIManager.bottomBar.children) {
        background = this.rexUIManager.bottomBar.children.find(child => child.type === 'Graphics' || child.fillColor !== undefined);
      }
      
      if (background && background.setFillStyle) {
        console.log('🎨 Updating bottom bar to darker Bain red');
        background.setFillStyle(bainSecondary);
      } else {
        console.log('❌ Bottom bar background element not accessible');
        console.log('Bottom bar structure:', this.rexUIManager.bottomBar);
      }
    } else {
      console.log('❌ Bottom bar not found');
    }
    
    // Update sidebar background - try different access methods  
    if (this.rexUIManager.leftSidebar || this.rexUIManager.sidebar) {
      const sidebar = this.rexUIManager.leftSidebar || this.rexUIManager.sidebar;
      
      // Try multiple ways to access background
      let background = sidebar.getElement('background');
      if (!background) {
        background = sidebar.background;
      }
      if (!background && sidebar.children) {
        background = sidebar.children.find(child => child.type === 'Graphics' || child.fillColor !== undefined);
      }
      
      if (background && background.setFillStyle) {
        console.log('🎨 Updating sidebar to Bain red');
        background.setFillStyle(bainPrimary);
      } else {
        console.log('❌ Sidebar background element not accessible');
        console.log('Sidebar structure:', sidebar);
      }
    } else {
      console.log('❌ Sidebar not found');
    }
  }

  // Override to create scrollable sidebar with Bain rounds
  createBainScrollableSidebar() {
    console.log('🔧 Creating Bain scrollable sidebar with additional rounds');
    
    // Destroy the existing generic sidebar
    if (this.rexUIManager.leftSidebar) {
      this.rexUIManager.leftSidebar.destroy();
    }
    
    // Create new scrollable sidebar with all Bain rounds
    const bainRounds = this.getRoundsDefinition().map(round => ({
      name: round.name,
      desc: round.description,
      color: round.color,
      id: round.id,
      levels: round.levels,
      firmSpecific: round.firmSpecific || false
    }));
    
    // Use the RexUIManager's scrollable sidebar creation with Bain rounds
    this.rexUIManager.createLeftSidebar(bainRounds);
    
    console.log('✅ Bain scrollable sidebar created with', bainRounds.length, 'rounds');
  }

  // Override setupLevels to add Bain-specific levels after the original 21
  setupLevels() {
    console.log("🔧 PPTHeroBainScene setupLevels: Starting");
    console.log("🔧 PPTHeroBainScene setupLevels: Before super.setupLevels(), this.levels length:", this.levels?.length);
    
    // Call parent to get original levels
    super.setupLevels();
    
    console.log("🔧 PPTHeroBainScene setupLevels: After super.setupLevels(), this.levels length:", this.levels?.length);
    
    // Add Bain-specific levels (22-30) - Testing Bain shortcuts
    const bainLevels = [
      // Level 22: CTRL + Shift + O - Add a sticker
      {
        id: 22,
        name: "Add Sticker",
        instructions: "🎯 CHALLENGE: Use the keyboard shortcut CTRL + Shift + O to add a sticker to your slide. Simply press the keys - no need to move shapes!",
        shapes: [
          { x: 300, y: 200, w: 120, h: 80, color: 0x3498db, type: 'rectangle' },
          { x: 500, y: 200, w: 120, h: 80, color: 0x2ecc71, type: 'rectangle' },
          { x: 400, y: 300, w: 100, h: 60, color: 0xf39c12, type: 'rectangle' }
        ],
        goal: (scene) => {
          // Goal: User successfully used the add sticker shortcut
          return scene.analyticsData.shortcutsUsed["ctrl,shift,o"] > 0;
        },
        timeLimit: 60,
        isShortcutLevel: true,
        shortcutToTest: "ctrl,shift,o"
      },

      // Level 23: CTRL + Shift + A - Add a status sticker
      {
        id: 23,
        name: "Add Status Sticker",
        instructions: "🎯 CHALLENGE: Use the keyboard shortcut CTRL + Shift + A to add a status sticker. Simply press the keys - no need to move shapes!",
        shapes: [
          { x: 250, y: 180, w: 100, h: 100, color: 0xe74c3c, type: 'circle' },
          { x: 400, y: 180, w: 100, h: 100, color: 0xf39c12, type: 'circle' },
          { x: 550, y: 180, w: 100, h: 100, color: 0x2ecc71, type: 'circle' },
          { x: 325, y: 320, w: 150, h: 60, color: 0x9b59b6, type: 'rectangle' }
        ],
        goal: (scene) => {
          return scene.analyticsData.shortcutsUsed["ctrl,shift,a"] > 0;
        },
        timeLimit: 60,
        isShortcutLevel: true,
        shortcutToTest: "ctrl,shift,a"
      },



      // Level 24: CTRL + SHIFT + B - Add bain bullets
      {
        id: 24,
        name: "Add Bain Bullets",
        instructions: "🎯 CHALLENGE: Use the keyboard shortcut CTRL + SHIFT + B to add Bain-style bullet points. Simply press the keys - no need to move shapes!",
        shapes: [
          { x: 200, y: 160, w: 160, h: 70, color: 0xc41e3a, type: 'rectangle' },
          { x: 400, y: 160, w: 160, h: 70, color: 0x1f4e79, type: 'rectangle' },
          { x: 600, y: 160, w: 160, h: 70, color: 0x2ecc71, type: 'rectangle' },
          { x: 300, y: 280, w: 200, h: 80, color: 0xf39c12, type: 'rectangle' }
        ],
        goal: (scene) => {
          return scene.analyticsData.shortcutsUsed["ctrl,shift,b"] > 0;
        },
        timeLimit: 60,
        isShortcutLevel: true,
        shortcutToTest: "ctrl,shift,b"
      },

      // Level 26: CTRL + SHIFT + H - Add headers to the slide
      {
        id: 25,
        name: "Add Headers",
        instructions: "🎯 CHALLENGE: Use the keyboard shortcut CTRL + SHIFT + H to add professional headers. Simply press the keys - no need to move shapes!",
        shapes: [
          { x: 250, y: 200, w: 120, h: 60, color: 0x9b59b6, type: 'rectangle' },
          { x: 400, y: 200, w: 120, h: 60, color: 0xe67e22, type: 'rectangle' },
          { x: 550, y: 200, w: 120, h: 60, color: 0x16a085, type: 'rectangle' },
          { x: 325, y: 320, w: 150, h: 80, color: 0x8e44ad, type: 'rectangle' }
        ],
        goal: (scene) => {
          return scene.analyticsData.shortcutsUsed["ctrl,shift,h"] > 0;
        },
        timeLimit: 60,
        isShortcutLevel: true,
        shortcutToTest: "ctrl,shift,h"
      },





      // Level 28: ALT,X,S,H - Standardize Heights to Triangle
      {
        id: 28,
        name: "Standardize Heights to Triangle",
        instructions: "🎯 CHALLENGE: Select the Triangle first, then select all squares. Use ALT, X, S, H to make all squares the same height as the Triangle.",
        shapes: [
          { type: "triangle", x: 300, y: 200, w: 80, h: 120, color: 0xf1c40f }, // Yellow triangle (reference height: 120)
          { type: "square", x: 450, y: 180, w: 80, h: 80, color: 0xe74c3c },     // Red square (shorter)
          { type: "square", x: 580, y: 170, w: 80, h: 140, color: 0x3498db },    // Blue square (taller) 
          { type: "square", x: 350, y: 320, w: 80, h: 90, color: 0x2ecc71 },     // Green square (shorter)
          { type: "square", x: 500, y: 330, w: 80, h: 160, color: 0x9b59b6 },    // Purple square (taller)
          { type: "square", x: 420, y: 280, w: 80, h: 70, color: 0xe67e22 }      // Orange square (shorter)
        ],
        goal: (scene) => {
          // Check both shortcut execution AND height standardization
          const shortcutUsed = scene.analyticsData.shortcutsUsed["alt,x,s,h"] > 0;
          const heightsStandardized = scene.selectionManager && scene.selectionManager.checkHeightStandardization(28);
          return shortcutUsed && heightsStandardized;
        },
        timeLimit: 60,
        isShortcutLevel: true,
        shortcutToTest: "alt,x,s,h"
      },

      // Level 29: ALT,X,S,W - Standardize Widths to Circle
      {
        id: 29,
        name: "Standardize Widths to Circle",
        instructions: "🎯 CHALLENGE: Select the Circle first, then select all squares. Use ALT, X, S, W to make all squares the same width as the Circle.",
        shapes: [
          { type: "circle", x: 320, y: 220, w: 130, h: 80, color: 0x3498db },    // Blue circle (reference width: 130)
          { type: "square", x: 480, y: 180, w: 90, h: 80, color: 0xe74c3c },     // Red square (narrower)
          { type: "square", x: 580, y: 200, w: 160, h: 80, color: 0x2ecc71 },    // Green square (wider)
          { type: "square", x: 400, y: 320, w: 70, h: 80, color: 0xf39c12 },     // Orange square (narrower)
          { type: "square", x: 520, y: 340, w: 180, h: 80, color: 0x9b59b6 },    // Purple square (wider)
          { type: "square", x: 380, y: 280, w: 100, h: 80, color: 0xe67e22 }     // Orange square (narrower)
        ],
        goal: (scene) => {
          // Check both shortcut execution AND width standardization
          const shortcutUsed = scene.analyticsData.shortcutsUsed["alt,x,s,w"] > 0;
          const widthsStandardized = scene.selectionManager && scene.selectionManager.checkWidthStandardization(29);
          return shortcutUsed && widthsStandardized;
        },
        timeLimit: 60,
        isShortcutLevel: true,
        shortcutToTest: "alt,x,s,w"
      }
    ];

    // Add the new Bain levels to the scene's levels array
    this.levels = this.levels.concat(bainLevels);
    
    // The LevelManager accesses levels via this.scene.levels, so ensure this is properly set
    // This is the key fix - the LevelManager needs to find levels in the scene's levels array
    console.log('Added', bainLevels.length, 'Bain-specific levels. Total levels:', this.levels.length);
    console.log('LevelManager access check - this.levels length:', this.levels.length);
    console.log('LevelManager access check - this.levelManager.scene.levels length:', this.levelManager?.scene?.levels?.length || 'undefined');
    
    // Debug: Check if LevelManager can access the levels
    if (this.levelManager) {
      console.log('🔧 LevelManager found, checking access to levels...');
      console.log('🔧 this.levelManager.scene === this:', this.levelManager.scene === this);
      console.log('🔧 this.levelManager.scene.levels:', this.levelManager.scene.levels);
      console.log('🔧 this.levelManager.scene.levels length:', this.levelManager.scene.levels?.length);
    } else {
      console.log('❌ LevelManager not found!');
    }
  }

  // Override handleResize to position Bain branding
  handleResize(width, height) {
    // Prevent resize handling during scene transitions
    if (this.scene.isActive && !this.scene.isActive()) {
      console.log('⚠️ Resize called on inactive scene, skipping');
      return;
    }
    
    super.handleResize(width, height);
    
    const actualWidth = width || this.cameras.main.width;
    
    // Add null checks for Bain-specific components
    if (this.bainBranding) {
      // Position on the slide area, below the HUD
      if (this.rexUIManager && this.rexUIManager.sidebarWidth) {
        this.bainBranding.setPosition(this.rexUIManager.sidebarWidth + 20, 120);
      }
    }
    
    if (this.bainModeIndicator) {
      this.bainModeIndicator.setPosition(actualWidth - 15, 120);
    }
  }
  
  // Override getFirmSpecificLevels to provide Bain-specific level mappings
  getFirmSpecificLevels(roundId) {
    switch (roundId) {
      case 'bain-basic':
        return [22, 23, 24, 25, 28, 29]; // Bain Basic levels - Levels 26,27 removed  
      case 'bain-advanced':
        return [31, 32, 33, 34, 35, 36, 37, 38, 39, 40]; // Bain Advanced levels
      default:
        console.log('Bain scene: Unknown firm-specific round:', roundId);
        return [];
    }
  }

  // Override parent method to provide additional browser shortcut prevention
  shouldPreventBrowserShortcut(event) {
    // Prevent all Bain-specific CTRL+SHIFT combinations (unconditional)
    if (event.ctrlKey && event.shiftKey) {
      const key = event.key.toLowerCase();
      if (['o', 'a', 'b', 'h'].includes(key)) {
        console.log(`🛡️ Bain shortcut prevention: CTRL+SHIFT+${key.toUpperCase()}`);
        return true;
      }
    }
    return false;
  }

  // Execute Bain shortcuts directly from document listener
  executeBainShortcut(key) {
    console.log(`🎯 Executing Bain shortcut for key: ${key}`);
    
    // Debug: Check what's available on gameStateManager
    if (this.gameStateManager) {
      console.log('🔍 gameStateManager methods:', Object.getOwnPropertyNames(Object.getPrototypeOf(this.gameStateManager)));
      console.log('🔍 gameStateManager properties:', Object.keys(this.gameStateManager));
    }
    
    // Check if we're in the right game state - use the correct method name
    if (this.gameStateManager && typeof this.gameStateManager.areShortcutsEnabled === 'function') {
      if (!this.gameStateManager.areShortcutsEnabled()) {
        console.log('❌ Shortcuts not enabled in current game state');
        return;
      }
    } else {
      // Fallback: check if we're in playing state
      if (this.gameState !== 'playing') {
        console.log('❌ Not in playing state, shortcuts disabled');
        return;
      }
    }
    
    // Execute the appropriate Bain shortcut
    const shortcutKey = `ctrl,shift,${key}`;
    if (this.getShortcuts && this.getShortcuts()[shortcutKey]) {
      console.log(`✅ Executing Bain shortcut: ${shortcutKey}`);
      this.getShortcuts()[shortcutKey]();
    } else {
      console.log(`❌ Bain shortcut not found: ${shortcutKey}`);
    }
    
    // ADDITIONAL DEBUG: Test if the shortcut method exists
    console.log('🔍 Available shortcut methods:', Object.getOwnPropertyNames(Object.getPrototypeOf(this)));
    console.log('🔍 getShortcuts result:', this.getShortcuts ? this.getShortcuts() : 'undefined');
  }


}
